import { useState } from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Shield } from "lucide-react";
import API from "@/lib/api";

export default function ForgotPassword() {
  const { branding } = useBranding();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const platformName = branding.platform_name || "MEP";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent");
    } catch {
      // Always show success to avoid email enumeration
      setSent(true);
      toast.success("If that email exists, a reset link has been sent");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page min-h-screen flex flex-col items-center justify-center relative overflow-hidden" data-testid="forgot-password-page">
      {/* Atmospheric background */}
      <div className="login-bg absolute inset-0" />
      <div className="login-glow-1 absolute w-[600px] h-[600px] rounded-full" />
      <div className="login-glow-2 absolute w-[400px] h-[400px] rounded-full" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-sm px-6 flex flex-col items-center">
        {/* Logo */}
        <div className="mb-10 text-center">
          <div className="logo-pulse-container mx-auto mb-5">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={platformName} className="h-24 mx-auto rounded-2xl" data-testid="forgot-logo" />
            ) : (
              <img src="/furd-logo-dark.png" alt="FURD Tech" className="h-24 rounded-2xl" data-testid="forgot-logo" />
            )}
          </div>
        </div>

        {!sent ? (
          <>
            {/* Reset form */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                <Shield className="w-5 h-5 text-blue-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2" data-testid="forgot-title">Reset your password</h1>
              <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">
                Enter your email address and we will send you instructions to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="w-full space-y-5" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="login-input h-12 pl-11 bg-slate-900/60 border-slate-800/80 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 rounded-lg text-sm"
                    placeholder="you@company.com"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold h-12 rounded-lg shadow-lg shadow-blue-500/10 transition-all text-sm tracking-wide"
                data-testid="forgot-submit-btn"
              >
                {submitting ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </>
        ) : (
          /* Success state */
          <div className="text-center" data-testid="forgot-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-5">
              <Mail className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-3">Check your email</h2>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto mb-2">
              If an account exists for <span className="text-white font-medium">{email}</span>, we have sent password reset instructions.
            </p>
            <p className="text-xs text-slate-600 leading-relaxed max-w-xs mx-auto">
              If you do not receive an email, please contact your administrator. Password resets are managed by your Director.
            </p>
          </div>
        )}

        {/* Back to login link */}
        <div className="mt-8 text-center">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-400 transition-colors" data-testid="back-to-login">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </div>

        {/* Admin contact note */}
        <div className="mt-6 w-full p-3.5 bg-slate-900/40 rounded-lg border border-slate-800/60">
          <p className="text-[11px] text-slate-500 leading-relaxed text-center">
            Passwords are managed by your Director. If you cannot access your account, ask your administrator to reset your password from Admin Settings.
          </p>
        </div>
      </div>
    </div>
  );
}
